import React from 'react';
import { getSiteCode } from '@/frontend/entrypoints/algolia';

// ================================================
// Lifted from src/theme/snippets/product-price.liquid
// -- probably should be re-styled in TWCSS
// ================================================

type UnitPriceMeasurement = {
  reference_value: number;
  reference_unit: string;
};

type Variant = {
  title?: string;
  compare_at_price: number;
  price: number;
  inventory_available: boolean;
  variants_min_price: number;
  variants_max_price: number;

  // Not in algolia
  unit_price?: number
  // Not in algoliar;
  unit_price_measurement?: UnitPriceMeasurement;

  tags: string[];
};

type Props = {
  variant?: Variant;
  source?: string;
};

export const ProductPrice: React.FC<Props> = ({ variant, source }) => {
  const siteCode = getSiteCode();
  const locale = siteCode === 'sg' ? 'en-SG' : 'en-AU';
  const compare_at_price = variant?.title ? Math.ceil(variant.compare_at_price) : 1999;
  const price = variant?.title ? Math.ceil(variant.price) : 1999;
  const available = variant?.title ? variant.inventory_available : true;
  const is_price_varies = variant?.price !== undefined && variant?.variants_max_price !== undefined && variant?.price !== variant.variants_max_price;
  const variants_min_price = `$${Math.ceil(variant?.price ?? 0).toLocaleString(locale, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  const money_price = `$${(price).toLocaleString(locale, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  const isInstoreOnly = variant?.tags.includes('non-transactional') ?? false;

  const renderUnitPrice = () => {
    if (!available || !variant?.unit_price_measurement) return null;

    const refVal = variant.unit_price_measurement.reference_value;
    const refUnit = variant.unit_price_measurement.reference_unit;

    return (
      <div className="price__unit">
        <dt>
          <span className="visually-hidden visually-hidden--inline">
            Unit price
          </span>
        </dt>
        <dd className="price-unit-price">
          <span data-unit-price>{`$${variant.unit_price!.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}</span>
          <span aria-hidden="true">/</span>
          <span className="visually-hidden">per </span>
          <span data-unit-price-base-unit>
            {refVal !== 1 && `${refVal} `}
            {refUnit}
          </span>
        </dd>
      </div>
    );
  };

  return (
    <dl
      className={`price tw-flex tw-flex-nowrap ${
        available && compare_at_price > price ? ' price--on-sale' : ''
      }${available && variant?.unit_price_measurement ? ' price--unit-available' : ''}`}
      data-price
    >
      <div className="price__regular">
        <dt>
          <span className="visually-hidden visually-hidden--inline">
            Regular price
          </span>
        </dt>
        <dd>
          {available ? (
            compare_at_price > price ? (
              <span className="price-item price-item--regular tw-text-nowrap" data-regular-price>
                {isInstoreOnly ? 'Custom Order' : <s>${compare_at_price.toLocaleString(locale, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</s>}
              </span>
            ) : (
              <span className="price-item price-item--regular tw-text-nowrap" data-regular-price>
                {isInstoreOnly ? 'Custom Order' : `${is_price_varies ? 'From ' + variants_min_price : money_price}`}
              </span>
            )
          ) : (
            <span className="price-item price-item--regular tw-text-nowrap" data-regular-price>
              Sold out
            </span>
          )}
        </dd>
      </div>

      <div className="price__sale">
        <dt>
          <span className="visually-hidden visually-hidden--inline">Sale price</span>
        </dt>
        <dd>
          <span className="price-item price-item--sale tw-text-nowrap" data-sale-price>
            {is_price_varies ? 'From ' + variants_min_price : money_price}
          </span>
          <span aria-hidden="true" className="price-item__label visually-hidden">
            On sale
          </span>
        </dd>
      </div>

      {renderUnitPrice()}
    </dl>
  );
};
